import React from 'react';
import parse from 'html-react-parser';
import { formatDateRangeJewish, formatDateJewish } from '../../utils/dateUtils';
import { renderKioskScheduleSection } from '../../utils/scheduleRenderer';
import { bidiText } from '../../utils/bidiText';
import styles from './WeeklyPrintSchedule.module.css';

/** The designed ?print view; all schedule rows use the existing print renderer. */
function WeeklyPrintSchedule({ printSchedule, announcements, weeklyNotices = [], reloadWithNextSaturday }) {
  const { dateRange, fullWeeklySchedules, parsha, chazak, mevorchim } = printSchedule;
  const candleLighting = fullWeeklySchedules?.fridayNight?.schedule?.find(item =>
    (item.zman === 'CANDLE_LIGHTING' || item.title?.includes('הדלקת נרות')) && !item.hideItem &&
    item.display !== 'screen_only' && item.display !== 'none'
  );
  const weekStart = dateRange ? new Date(dateRange.start) : null;
  if (weekStart) weekStart.setDate(weekStart.getDate() + 1);

  // Keep the editor's visibility settings, without an empty card when every
  // announcement block is hidden. Other HTML and inline formatting are retained.
  const announcementContent = React.Children.toArray(parse(announcements || '')).filter(node =>
    typeof node === 'string' ? node.trim() : node.props?.style?.display !== 'none'
  );

  return (
    <>
      <nav className={styles.toolbar} aria-label="Print schedule navigation">
        <button type="button" onClick={reloadWithNextSaturday}>Next Week Schedule</button>
      </nav>

      <article className={styles.sheet} aria-label="Weekly shul schedule">
        <header className={styles.masthead}>
          <div className={styles.branding}>
            <img className={styles.logo} src="/logo.png" alt="Beis Levi Yitzchok logo" />
            <div>
              <div className={styles.shulName}>Beis Levi Yitzchok</div>
              <div className={styles.location}>Pomona, NY</div>
            </div>
          </div>
          <div className={styles.weekHeading} dir="rtl">
            <div className={styles.bsd}>בס״ד</div>
            <h1>{parsha}{chazak ? ' - חזק' : ''}</h1>
            {dateRange && (
              <div className={styles.shabbosDate}>
                {mevorchim ? 'שבת מברכים' : 'שבת קודש'} · {formatDateJewish(dateRange.start)}
              </div>
            )}
          </div>
        </header>

        <div className={styles.dateStrip} dir="rtl">
          {candleLighting?.value && (
            <div className={styles.candleLighting}>
              <svg viewBox="0 0 20 32" aria-hidden="true" focusable="false">
                <path d="M10 2C7 6 5.5 8 5.5 11a4.5 4.5 0 0 0 9 0C14.5 8 13 6 10 2Z M6 18h8v12H6Z M10 15v3" />
              </svg>
              <span>הדלקת נרות ערב שבת</span>
              <strong dir="ltr">{bidiText(candleLighting.value)}</strong>
            </div>
          )}
          {dateRange && <div className={styles.weekRange}>שבוע של {formatDateRangeJewish(weekStart, dateRange.end)}</div>}
        </div>

        <div className={styles.columns}>
          <div className={styles.column}>
            {renderKioskScheduleSection(printSchedule, 'fridayNight')}
            {renderKioskScheduleSection(printSchedule, 'shabbosDay')}
            {renderKioskScheduleSection(printSchedule, 'shabbosAfternoon')}
          </div>
          <div className={styles.column}>
            {renderKioskScheduleSection(printSchedule, 'sunday')}
            {renderKioskScheduleSection(printSchedule, 'weekdays')}

            {weeklyNotices.length > 0 && (
              <section className={styles.notices} dir="rtl">
                <h2>הודעות</h2>
                <ul>
                  {weeklyNotices.map((notice, index) => <li key={index}>{bidiText(notice)}</li>)}
                </ul>
              </section>
            )}

            {announcementContent.length > 0 && (
              <section className={styles.announcements}>
                <h2 dir="rtl">בקהילה</h2>
                <div className={styles.announcementContent}>{announcementContent}</div>
              </section>
            )}
          </div>
        </div>
      </article>
    </>
  );
}

export default WeeklyPrintSchedule;
